import {
	StyledPillButton,
	colors,
	focusVisible,
	measures,
	text,
} from "@quickpoint.me/shared";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { EditorIsClean, HostId, RenameFamily } from "../../state/atoms";
import {
	CreateNewStack,
	ShowStacks,
	StackCfKeyAtom,
} from "../../state/stackAtoms";
import {
	MousedOverTextHelper,
	OpenedInTabStories,
	Orientation,
} from "../../state/uiAtoms";

import { Suspense, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { useSaveStack } from "../../hooks/useSaveStack";
import FileIcon from "../../icons/file.svg?react";
import Rename from "../../icons/pen.svg?react";
import Trash from "../../icons/trash.svg?react";
import { deleteStack } from "../../lib/messageBus";
import { CurrentlySelectedFilePath } from "../../state/files";
import RefreshSpinner from "../Refresh";
import { EditableFile } from "./EditableFile";
import { useMetrics } from "../../hooks/useMetrics";

const landscapeColumns = "5fr 15fr 40fr 15fr 15fr";
const portraitColumns = "10fr 10fr 80fr";

const StyledConfirmBackdrop = styled.div`
	position: fixed;
	inset: 0;
	z-index: 2001;
	background: rgba(0, 0, 0, 0.35);
	display: flex;
	align-items: center;
	justify-content: center;
`;

const StyledConfirmDialog = styled.div`
	background: white;
	border-radius: 8px;
	box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
	padding: 28px 32px;
	max-width: 26rem;
	width: 90%;
	font-family: inherit;

	p {
		margin: 0 0 1.2rem;
		color: #333;
		line-height: 1.5;
	}
	.confirm-buttons {
		display: flex;
		justify-content: flex-end;
		gap: 0.6rem;
	}
`;

export const StyledFile = styled.div<{
	type?: "folder";
	$isInLandscape: boolean;
	$noRollOver?: boolean;
}>`
    align-items: center;
    padding: 1rem;
    display: grid;
    grid-template-columns: ${(props) => (props.$isInLandscape ? landscapeColumns : portraitColumns)}; 
    border-block-end: 1px solid ${(props) => props.theme.navigationFrame};
    font-family: ${(props) => props.theme.font};
    &.files-header {
       position: sticky;
       top:0;
       background: ${(props) => props.theme.stacksHeaderBackGround};
       button {
         min-height: 48px;
         cursor: pointer;
       }
    }

    .form-row {
        display: flex;
        flex-direction: row;
        align-items: center;
    }
    &:hover {
      background-color: ${(props) => (props.$noRollOver ? "" : props.theme.stacksRollOverBackground)};
    }

    .form-input-stack-name {
        display: flex;
        align-items: center;
     }
 
    .form-input-stack-name > input {
        width: 90%;    
        font-family: ${(props) => props.theme.font};
        padding: .5svw;
        border: 1px solid ${(props) => props.theme.navigationFrame};
        border-radius: .5svw;
        width: 100%
         
    }

    .form-input-submit{
      display: flex;
      flex-direction: row;
      align-items: center;
      column-gap: 0.5rem;
      padding-inline: ${(props) =>
				props.$isInLandscape
					? measures.dialogPanel.landscape.margin
					: measures.dialogPanel.portrait.margin};
  }

    .form-owner-name {
      padding-inline-end: 1%;
      
    }

    .hint-section {
      color: ${colors.textSafe.grey};
    }

    .icon-section {
      flex-basis: 20%
    }

    .file-created, .file-updated {
      color: ${colors.textSafe.grey};
      font-size: 0.8rem;
    }


    .file-link {
      display: flex;
      cursor: pointer;
      min-height: 48px;
      white-space: nowrap;
      align-items: center;
      justify-items: start;
      flex-basis: 50%;
      ${focusVisible}
    }

    .sort-indicator {
      color: ${colors.brand.purple};
      font-size: 0.6rem;
      margin-inline-start: 0.5rem;
    }

    .text {
      padding-inline-start: 1rem;
    }

    /* icon actions are real buttons: reset chrome, keep a generous target */
    .rename, .trash {
        cursor: pointer;
        background: none;
        border: none;
        padding: 0;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-width: 48px;
        min-height: 48px;
        justify-self: start;
        ${focusVisible}
    }

    .close {
        cursor: pointer;
      
    }
`;

export function File({
	stackCfKey,
	stackFileName,
	created,
	updated,
}: {
	stackCfKey: string;
	stackFileName: string;
	created: string;
	updated: string;
}) {
	const hostId = useRecoilValue(HostId);
	const setTextHelper = useSetRecoilState(MousedOverTextHelper);
	const setStackCfKey = useSetRecoilState(StackCfKeyAtom);
	const setShowStacks = useSetRecoilState(ShowStacks);
	const [rename, setRename] = useRecoilState(RenameFamily(stackCfKey));
	const setCreateNewStack = useSetRecoilState(CreateNewStack);
	const currentlySelectedFilePath = useRecoilValue(CurrentlySelectedFilePath);
	const editorIsClean = useRecoilValue(EditorIsClean);
	const isInLandscape = useRecoilValue(Orientation) === "landscape";
	const setOpenedInTabStories = useSetRecoilState(OpenedInTabStories);
	const saveStack = useSaveStack();
	const { track } = useMetrics();

	const getStack = async (
		stackCfKey: string,
		e:
			| React.MouseEvent<HTMLDivElement, MouseEvent>
			| React.KeyboardEvent<HTMLDivElement>,
	) => {
		const openFile = () => {
			setStackCfKey(stackCfKey);
			if (e.shiftKey) {
				setOpenedInTabStories((prev) =>
					prev.includes(stackCfKey) ? prev : [...prev, stackCfKey],
				);
			} else {
				setOpenedInTabStories([stackCfKey]);
			}
			setShowStacks(false);
			track('story_opened', { stackCfKey, stackFileName });
		};

		if (editorIsClean) {
			openFile();
		} else {
			await saveStack();
			openFile();
		}
	};

	// styled confirm dialog instead of window.confirm, per the app's dialogs
	const [confirmingDelete, setConfirmingDelete] = useState(false);
	const cancelDeleteRef = useRef<HTMLButtonElement>(null);
	useEffect(() => {
		if (confirmingDelete) cancelDeleteRef.current?.focus();
	}, [confirmingDelete]);

	const confirmDelete = () => {
		setConfirmingDelete(false);
		if (!currentlySelectedFilePath) return;
		deleteStack(hostId, stackCfKey);
	};

	const mouseOverTrash = () => {
		setTextHelper(text.controls.trash);
	};

	const mouseLeaveTrash = () => {
		setTextHelper(undefined);
	};

	const mouseOverRename = () => {
		setTextHelper(text.controls.trash);
	};

	const mouseLeaveRename = () => {
		setTextHelper(undefined);
	};

	if (rename) {
		return (
			<EditableFile
				stackCfKey={stackCfKey}
				oldStackCfKey={stackCfKey}
				renaming={rename}
			/>
		);
	}

	return (
		<Suspense fallback={<RefreshSpinner showBackGround={true} />}>
			<StyledFile $isInLandscape={isInLandscape}>
				<button
					type="button"
					data-testid={`rename-${stackFileName}`}
					onClick={() => {
						setRename(true);
						setCreateNewStack(false);
					}}
					onMouseOver={mouseOverRename}
					onMouseLeave={mouseLeaveRename}
					onFocus={mouseOverRename}
					onBlur={mouseLeaveRename}
					aria-label={text.controls.rename}
					className="file-control rename"
				>
					<Rename aria-hidden="true" focusable="false" />
				</button>
				<button
					type="button"
					data-testid={`trash-${stackFileName}`}
					onClick={() => setConfirmingDelete(true)}
					onMouseOver={mouseOverTrash}
					onMouseLeave={mouseLeaveTrash}
					onFocus={mouseOverTrash}
					onBlur={mouseLeaveTrash}
					aria-label={text.controls.trash}
					className="file-control trash"
				>
					<Trash aria-hidden="true" focusable="false" className="trash-icon" />
				</button>

				<div
					className="file-link"
					data-testid={`file-link-${stackFileName}`}
					onClick={(e) => getStack(stackCfKey, e)}
					onKeyDown={async (e) => {
						if (e.key === "Enter" || e.key === " ") {
							e.preventDefault();
							await getStack(stackCfKey, e);
						}
					}}
					role="button"
					tabIndex={0}
					id={`link-to-${stackCfKey}`}
				>
					<FileIcon
						aria-label={text.controls.file}
						aria-hidden="true"
						focusable="false"
						className="file-icon"
					/>
					<div className="file text">{stackFileName}</div>
				</div>
				{isInLandscape && (
					<>
						<div className="file-updated">{updated}</div>
						<div className="file-created">{created}</div>
					</>
				)}
				<div />
			</StyledFile>
			{confirmingDelete && (
				<StyledConfirmBackdrop onClick={() => setConfirmingDelete(false)}>
					<StyledConfirmDialog
						role="dialog"
						aria-modal="true"
						aria-label={(text.ui.deleteConfirm as (name: string) => string)(
							stackFileName,
						)}
						onClick={(e) => e.stopPropagation()}
						onKeyDown={(e) => {
							if (e.key === "Escape") setConfirmingDelete(false);
						}}
					>
						<p>
							{(text.ui.deleteConfirm as (name: string) => string)(
								stackFileName,
							)}
						</p>
						<div className="confirm-buttons">
							<StyledPillButton
								$variant="primaryOutline"
								ref={cancelDeleteRef}
								onClick={() => setConfirmingDelete(false)}
							>
								{text.ui.cancel}
							</StyledPillButton>
							<StyledPillButton
								$variant="danger"
								data-testid={`confirm-delete-${stackFileName}`}
								onClick={confirmDelete}
							>
								{text.stacks.delete}
							</StyledPillButton>
						</div>
					</StyledConfirmDialog>
				</StyledConfirmBackdrop>
			)}
		</Suspense>
	);
}
